"use client"

import { useId } from "react"
import Link from "next/link"
import { useRecentPlays, type RecentPlay } from "@/hooks/useRecentPlays"
import { splitTitle } from "@/lib/plate-and-hairline"
import { soundUrl } from "@/lib/slugify"

/** Five titles on a wide screen; the first three on a phone, where the list has to clear the fold. */
const MAX_TITLES = 5
const PHONE_TITLES = 3

const LINK_CLASS =
  "tlsr-focus tls-touch-pad font-semibold text-[var(--tls-fg-muted)] underline decoration-[var(--tls-border-strong)] underline-offset-4 transition-colors hover:text-[var(--tls-fg-strong)]"

export interface RecentlyPlayedLink {
  id: string
  title: string
  href: string
}

/**
 * Newest first, one per sound, display portion of the title only ("Vine Boom",
 * not "Vine Boom (dramatic bass sound)"). Entries written before titles were
 * stored have nothing to show and are skipped.
 */
export function recentlyPlayedLinks(plays: readonly RecentPlay[], limit = MAX_TITLES): RecentlyPlayedLink[] {
  const seen = new Set<string>()
  const links: RecentlyPlayedLink[] = []
  for (const play of [...plays].sort((a, b) => b.ts - a.ts)) {
    if (links.length >= limit) break
    const title = play.title ? splitTitle(play.title).display : ""
    if (!title || seen.has(play.id)) continue
    seen.add(play.id)
    links.push({ id: play.id, title, href: soundUrl({ id: play.id, slug: play.slug }) })
  }
  return links
}

/**
 * A type-only line of the last few sounds this browser played. It reads
 * localStorage through useRecentPlays, whose server snapshot is empty, so the
 * server render and the hydration pass both render nothing and the line
 * appears only once storage has been read. It sits under the h1 in the title
 * column, which is sized by the grid and not by its content, so it never
 * changes the width the plate is solved against.
 */
export default function RecentlyPlayed({ className = "" }: { className?: string }) {
  const links = recentlyPlayedLinks(useRecentPlays())
  const labelId = useId()

  if (links.length === 0) return null

  return (
    <div className={`m-0 mt-4 flex flex-wrap items-baseline gap-x-4 gap-y-3 text-sm ${className}`} data-recently-played>
      <span id={labelId} className="font-semibold text-[var(--tls-fg-subtle)]">
        Recently played
      </span>
      <ul aria-labelledby={labelId} className="m-0 flex min-w-0 list-none flex-wrap items-baseline gap-x-4 gap-y-3 p-0">
        {links.map((link, i) => (
          <li key={link.id} className={i >= PHONE_TITLES ? "hidden sm:block" : undefined}>
            <Link prefetch={false} href={link.href} className={LINK_CLASS}>
              {link.title}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  )
}
